/**
 * NetworkCapacitySection
 *
 * The "SimCheck network" half of the Edges page: rows are LOCATIONS, showing
 * how much test capacity each one has. Rows deliberately do not open a device
 * view — tenants see shared infrastructure as capacity, never as devices.
 * See plans/edges-page-shared-vs-owned.md.
 */

import React, { useMemo } from 'react';
import { MapPin, Flag, Signal, Globe, Building2 } from 'lucide-react';
import { Badge, Card, Popover, PopoverTrigger, PopoverContent } from '@/components/ui';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import {
  useDataTableFilters,
  DataTableFilter,
  createColumnConfigHelper,
  applyFilters,
} from '@/components/data-table-filter';
import { MobileFilterSheet } from '@/components/layout/MobileFilterSheet';
import { useIsMobile } from '@/hooks/use-mobile';
import type { CapacityLocation, NetworkInfo } from '@/hooks/useNetworkCapacity';
import { CapabilitiesCell } from '@/components/locations/CapabilitiesCell';

const filterColumnHelper = createColumnConfigHelper<CapacityLocation>();

interface NetworkCapacitySectionProps {
  locations: CapacityLocation[];
  loading: boolean;
}

export const NetworkCapacitySection: React.FC<NetworkCapacitySectionProps> = ({
  locations,
  loading,
}) => {
  const isMobile = useIsMobile();

  const filterColumnsConfig = useMemo(() => {
    const countries = Array.from(
      new Set(locations.map((l) => l.countryName).filter(Boolean)),
    ).sort();
    const technologies = Array.from(
      new Set(locations.flatMap((l) => l.aggregatedCapabilities?.technologies || [])),
    ).sort();

    return [
      filterColumnHelper
        .text()
        .id('displayName')
        .accessor((row) => row.displayName || row.name || '')
        .displayName('Location')
        .icon(MapPin)
        .build(),
      filterColumnHelper
        .text()
        .id('city')
        .accessor((row) => row.city || '')
        .displayName('City')
        .icon(MapPin)
        .build(),
      filterColumnHelper
        .option()
        .id('countryName')
        .accessor((row) => row.countryName || '')
        .displayName('Country')
        .icon(Flag)
        .options(countries.map((c) => ({ label: c, value: c })))
        .build(),
      filterColumnHelper
        .option()
        .id('type')
        .accessor((row) => row.type)
        .displayName('Type')
        .icon(Building2)
        .options([
          { label: 'Datacenter', value: 'datacenter' },
          { label: 'Office', value: 'office' },
          { label: 'Lab', value: 'lab' },
          { label: 'Home', value: 'home' },
          { label: 'Mobile', value: 'mobile' },
        ])
        .build(),
      ...(technologies.length
        ? [
            filterColumnHelper
              .option()
              .id('technology')
              .accessor((row) => row.aggregatedCapabilities?.technologies || [])
              .displayName('Technology')
              .icon(Signal)
              .options(technologies.map((t) => ({ label: t, value: t })))
              .build(),
          ]
        : []),
    ];
  }, [locations]);

  const { columns: filterColumns, filters, actions, strategy } = useDataTableFilters({
    strategy: 'client',
    data: locations,
    columnsConfig: filterColumnsConfig,
  });

  const filteredLocations = useMemo(
    () => applyFilters(locations, filters, filterColumnsConfig),
    [locations, filters, filterColumnsConfig],
  );

  const totalAvailable = locations.reduce((sum, l) => sum + (l.availableModemCount || 0), 0);
  const totalModems = locations.reduce((sum, l) => sum + (l.modemCount || 0), 0);

  const columns = [
    {
      key: 'displayName',
      header: 'Location',
      sortable: true,
      render: (_value: any, location: CapacityLocation) => (
        <div className="flex items-center space-x-2">
          <MapPin className="w-4 h-4 text-primary-500" />
          <div>
            <div className="font-medium text-foreground">
              {location?.displayName || location?.name || '-'}
            </div>
            <div className="text-xs text-muted-foreground/70">{location?.name || '-'}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'regionName',
      header: 'Region',
      sortable: true,
      render: (_value: any, location: CapacityLocation) => (
        <div className="flex items-center space-x-2">
          <Globe className="w-4 h-4 text-accent-blue" />
          <span className="text-sm text-muted-foreground">{location?.regionName || '-'}</span>
        </div>
      ),
    },
    {
      key: 'countryName',
      header: 'Country',
      sortable: true,
      render: (_value: any, location: CapacityLocation) => (
        <div className="flex items-center space-x-2">
          <Flag className="w-4 h-4 text-accent-purple" />
          <span className="text-sm text-muted-foreground">
            {location?.countryFlag || ''} {location?.countryName || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'city',
      header: 'City',
      sortable: true,
      render: (_value: any, location: CapacityLocation) => (
        <span className="text-sm text-muted-foreground">{location?.city || '-'}</span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (_value: any, location: CapacityLocation) => (
        <Badge variant={location?.type === 'datacenter' ? 'default' : 'secondary'}>
          {location?.type || '-'}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_value: any, location: CapacityLocation) => (
        <Badge variant={location?.status === 'active' ? 'success' : 'error'}>
          {location?.status || 'unknown'}
        </Badge>
      ),
    },
    {
      key: 'modems',
      header: 'Modems',
      sortable: true,
      render: (_value: any, location: CapacityLocation) => (
        <div className="text-sm">
          <span className="font-medium text-foreground">
            {location?.availableModemCount || 0}
          </span>
          <span className="text-muted-foreground/70">
            {' / '}
            {location?.modemCount || 0}
          </span>
          <span className="text-xs text-muted-foreground/70 ml-1">available</span>
        </div>
      ),
    },
    {
      key: 'networks',
      header: 'Networks',
      render: (_value: any, location: CapacityLocation) => {
        const availableNetworks = location.availableNetworks as NetworkInfo[] | undefined;
        if (!availableNetworks || availableNetworks.length === 0) {
          return <span className="text-muted-foreground/70 text-sm">No data</span>;
        }
        const sorted = [...availableNetworks].sort((a, b) =>
          a.networkName.localeCompare(b.networkName),
        );
        return (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="text-sm text-foreground hover:text-primary-500 transition-colors underline-offset-2 hover:underline"
              >
                {sorted.length} network{sorted.length === 1 ? '' : 's'}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-0">
              <div className="px-3 py-2 border-b border-border">
                <p className="text-sm font-medium text-foreground">Detected networks</p>
                <p className="text-xs text-muted-foreground">
                  From the latest modem scans at this location
                </p>
              </div>
              <ul className="max-h-64 overflow-y-auto py-1">
                {sorted.map((n) => (
                  <li
                    key={n.networkId}
                    className="flex items-center justify-between gap-2 px-3 py-1.5"
                  >
                    <span className="text-sm text-foreground truncate">{n.networkName}</span>
                    {(n.mcc || n.mnc) && (
                      <Badge
                        variant="outline"
                        className="text-xs px-1.5 py-0 shrink-0 font-mono"
                      >
                        {n.mcc} {n.mnc}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        );
      },
    },
    {
      key: 'capabilities',
      header: 'Capabilities',
      render: (_value: any, location: CapacityLocation) => (
        <CapabilitiesCell capabilities={location.aggregatedCapabilities} />
      ),
    },
  ];

  return (
    <Card className="overflow-hidden">
      {/* Section header — same shape as the other card sections in the app
          (icon + title + count badge), so this page reads as part of the
          platform rather than a bare stack of tables. */}
      <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Globe className="w-5 h-5 text-primary" />
          <h2 className="font-semibold text-foreground">SimCheck network</h2>
          <Badge variant="outline" className="text-xs">
            {locations.length} location{locations.length === 1 ? '' : 's'}
          </Badge>
        </div>
        {locations.length > 0 && (
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{totalAvailable}</span> of{' '}
            {totalModems} modems available
          </p>
        )}
      </div>

      <p className="px-4 pb-4 text-sm text-muted-foreground">
        Locations you can run tests at. The modems here are operated by SimCheck — you pick a
        location, we pick the modem.
      </p>

      {filterColumnsConfig.length > 0 && (
        <div className="border-t border-border p-4">
          <MobileFilterSheet activeCount={filters.length}>
            <DataTableFilter
              columns={filterColumns}
              filters={filters}
              actions={actions}
              strategy={strategy}
            />
          </MobileFilterSheet>
        </div>
      )}

      <div className="border-t border-border p-4">
        <ResponsiveTable
          columns={columns}
          data={filteredLocations}
          keyExtractor={(row) => row.id}
          searchable
          searchPlaceholder="Search by location, city, or country..."
          emptyMessage={
            locations.length === 0
              ? 'No locations are available to your organization yet.'
              : 'No locations match your filters'
          }
          loading={loading}
          paginated
          defaultPageSize={isMobile ? 5 : 10}
        />
      </div>
    </Card>
  );
};
